/* eslint-disable */
import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import Framework from '@greenville/framework';
import {
  Typography,
  Paper,
  Grid,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from '@material-ui/core';

import utils from '../../../common/utils';
import { AGENTIC_LIVE_PUBLISH_LABEL, PROMPT_LIVE_STATUS } from '../../../common/constants';

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1001;
`;

const ModalContainer = styled.div`
  background-color: #fff;
  padding: 24px;
  border-radius: 8px;
  width: 700px;
  max-height: 80vh;
  height: fit-content;
  overflow-y: auto;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  position: relative;
  display: flex;
  flex-direction: column;
`;

const ModalHeader = styled.h2`
  margin: 0 0 16px 0;
  font-size: 1.5em;
  color: #1a1a1a;
  font-weight: 500;
`;

const ModalLabel = styled.label`
  margin: 0;
  font-size: 0.9em;
  color: #555;
  display: flex;
  align-items: center;
`;

const ModalInput = styled.input`
  width: 100%;
  padding: 10px;
  margin-bottom: 24px;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: 1em;
  box-sizing: border-box;
  &:focus {
    outline: none;
    border-color: #007bff;
    box-shadow: 0 0 5px rgba(0, 123, 255, 0.5);
  }
`;

const ModalRadio = styled.input`
  font-size: 1em;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
`;

const ModalButton = styled.button`
  padding: 8px 16px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.9em;
  transition: background-color 0.2s;

  &:first-child {
    background-color: #f0f0f0;
    color: #333;
    &:hover {
      background-color: #e0e0e0;
    }
  }

  &:last-child {
    background-color: #007bff;
    color: #fff;
    &:hover {
      background-color: #0056b3;
    }
  }
`;
const ModalDefaultCheckContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: 8px;
  margin-bottom: 24px;
`;

const modalConfigs = {
  graphNameChanges: {
    title: 'Graph Name',
    label: 'Please enter a name for your graph:',
    placeholder: 'Enter graph name',
    inputValidator: (val) => /^[a-zA-Z0-9_]+$/.test(val),
    errorMessage: 'Invalid graph name. Please use only letters, numbers, or underscores.',
    buttonOkLabel: 'OK',
    required: true,
    validateOnEmpty: true
  },
  saveChanges: {
    title: 'Save Changes to New Version',
    label:
      'There is already a LIVE version exist for this Graph, any changes will create a new version. Are you sure, you want to proceed?',
    placeholder: 'Enter your comment',
    inputValidator: (val) => /^[a-zA-Z0-9 ,.!?'\"()\\-]*$/.test(val),
    errorMessage:
      'Special characters are not allowed in comments. Please use only letters, numbers, spaces, and basic punctuation.',
    buttonOkLabel: 'OK',
    required: false,
    validateOnEmpty: false
  },
  publishChanges: {
    title: 'Publish Changes',
    label: 'Are you sure, you want to Publish?',
    placeholder: 'Enter your comment',
    inputValidator: (val) => /^[a-zA-Z0-9 ,.!?'\"()\\-]*$/.test(val),
    errorMessage:
      'Special characters are not allowed in comments. Please use only letters, numbers, spaces, and basic punctuation.',
    buttonOkLabel: 'OK',
    required: false,
    validateOnEmpty: false
  }
};

const ModalPopup = ({
  close,
  select,
  config,
  selectedDefault,
  defaultVersion,
  currentLiveVersionList,
  currentGraphStatus
}) => {
  const resolvedConfig = modalConfigs[config] || modalConfigs['graphNameChanges'];
  const {
    title,
    label,
    placeholder,
    inputValidator,
    errorMessage,
    buttonOkLabel,
    required = true,
    validateOnEmpty = true
  } = resolvedConfig;
  // const [graphName, setGraphName] = useState('');
  const [inputValue, setInputValue] = useState('');
  const [isDefault, setIsDefault] = useState(selectedDefault ?? false);
  const [defaultVersionVal, setDefaultVersion] = useState(defaultVersion ?? '');

  useEffect(() => {
    setIsDefault(selectedDefault ?? false);
  }, [selectedDefault]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (typeof event.target.className === 'string' && event.target.className.includes('modal-overlay')) {
        console.log('Clicked outside, closing modal');
        close();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [close]);

  const handleSelect = () => {
    if (required && !inputValue.trim()) {
      Framework.getStoreRegistry().getStore('notificationState').set({
        open: true,
        type: 'error',
        message: 'This field is required.'
      });
      return;
    }
    if ((validateOnEmpty || inputValue.trim()) && inputValidator && !inputValidator(inputValue)) {
      Framework.getStoreRegistry()
        .getStore('notificationState')
        .set({
          open: true,
          type: 'error',
          message: errorMessage || 'Invalid input.'
        });
      return;
    }
    select(inputValue, isDefault, defaultVersionVal);
    close();
  };

  const handleMultipleVersions = (isDefault, version = '') => {
    setIsDefault(isDefault);
    setDefaultVersion(version);
  };

  return (
    <ModalOverlay className="modal-overlay">
      <ModalContainer>
        <ModalHeader>{title}</ModalHeader>
        <ModalLabel>{currentGraphStatus === PROMPT_LIVE_STATUS ? AGENTIC_LIVE_PUBLISH_LABEL : label}</ModalLabel>
        <ModalInput
          type="text"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          autoFocus
          placeholder={placeholder}
        />
        {config === 'publishChanges' && (
          <>
            {defaultVersion && <ModalLabel>{`Current Default Version: ${defaultVersion}`}</ModalLabel>}

            {currentLiveVersionList?.length < 2 ? (
              <ModalDefaultCheckContainer>
                <ModalLabel>
                  <ModalRadio type="radio" name="defaultVersion" onChange={() => handleMultipleVersions(true)} />
                  Set as default
                </ModalLabel>
              </ModalDefaultCheckContainer>
            ) : (
              <TableContainer component={Paper} sx={{ border: '1px solid #ccc' }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell align="center">
                        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                          Version
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                          Last Modified Date
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                          Modified By
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                          Default
                        </Typography>
                      </TableCell>
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    <TableRow key="00">
                      <TableCell align="center">
                        <Typography variant="body2" style={{ fontSize: '12px', color: '#000' }}>
                          This Version
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Typography variant="body2" style={{ fontSize: '12px', color: '#000' }}></Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Typography variant="body2" style={{ fontSize: '12px', color: '#000' }}></Typography>
                      </TableCell>
                      <TableCell align="center">
                        <ModalRadio type="radio" name="defaultVersion" onChange={() => handleMultipleVersions(true)} />
                      </TableCell>
                    </TableRow>
                    {currentLiveVersionList?.map((row, index) => (
                      <TableRow key={index}>
                        <TableCell align="center">
                          <Typography variant="body2" style={{ fontSize: '12px', color: '#000' }}>
                            {row.version}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Typography variant="body2" style={{ fontSize: '12px', color: '#000' }}>
                            {utils.convertServerDateTime(row.updatedDate) || 'N/A'}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Typography variant="body2" style={{ fontSize: '12px', color: '#000' }}>
                            {row.modifiedBy || 'N/A'}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <ModalRadio
                            type="radio"
                            name="defaultVersion"
                            checked={defaultVersionVal === row?.version}
                            onChange={() => handleMultipleVersions(true, row?.version)}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </>
        )}
        <br />
        <ButtonGroup>
          <ModalButton onClick={close}>Cancel</ModalButton>
          <ModalButton onClick={handleSelect}>{buttonOkLabel}</ModalButton>
        </ButtonGroup>
      </ModalContainer>
    </ModalOverlay>
  );
};

export default ModalPopup;
